import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  TextField,
  CircularProgress,
  Snackbar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Storage,
  CloudQueue,
  Security,
  Speed,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { integrationsApi, importApi, Integration, ImportJob } from '../../../services/dataOnboardingApi';

interface DataIntegrationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const DataIntegrationStep: React.FC<DataIntegrationStepProps> = ({
  onNext,
  data,
  updateData,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  
  // Data states
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState<Set<string>>(new Set());
  const [uploadedFile, setUploadedFile] = useState<ImportJob | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Form states for ERP connection
  const [erpDetails, setErpDetails] = useState({
    system: data.erpSystem || '',
    apiKey: data.erpApiKey || '',
  });
  
  // UI states
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  
  // Fetch available integrations on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await integrationsApi.listIntegrations();
      setAvailableIntegrations(response.integrations);
      
      // Pre-select any already connected integrations
      const connected = new Set(
        response.integrations
          .filter(i => i.status === 'connected')
          .map(i => i.id)
      );
      setSelectedIntegrations(connected);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load integrations. Using offline mode.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIntegration = (integrationId: string) => {
    setSelectedIntegrations(prev => {
      const next = new Set(prev);
      if (next.has(integrationId)) {
        next.delete(integrationId);
      } else {
        next.add(integrationId);
      }
      return next;
    });
  };

  const handleConnectIntegration = async (integration: Integration) => {
    if (!erpDetails.apiKey && integration.auth_type === 'api_key') {
      setSnackbar({
        open: true,
        message: 'Please enter your API key',
        severity: 'error',
      });
      return;
    }

    try {
      setConnecting(integration.id);
      await integrationsApi.connect({
        integration_id: integration.id,
        api_key: erpDetails.apiKey,
        sync_frequency: 'daily',
        sync_products: true,
        sync_inventory: true,
        sync_orders: true,
        sync_suppliers: true,
      });
      
      setSnackbar({
        open: true,
        message: `Successfully connected to ${integration.name}!`,
        severity: 'success',
      });
      
      // Refresh the list
      await fetchIntegrations();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || `Failed to connect to ${integration.name}`,
        severity: 'error',
      });
    } finally {
      setConnecting(null);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const isValid = validTypes.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Invalid file type. Please upload a CSV or Excel file.',
        severity: 'error',
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate progress for UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload file
      const result = await importApi.uploadFile(file, 'products');
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadedFile(result);

      // Auto-preview after upload
      const preview = await importApi.previewImport(result.id);
      setPreviewData(preview);

      setSnackbar({
        open: true,
        message: `File "${file.name}" uploaded successfully! ${preview.total_rows} rows detected.`,
        severity: 'success',
      });

      // Show preview dialog
      setPreviewDialog(true);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to upload file',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadTemplate = async (dataType: string) => {
    try {
      const template = await importApi.getTemplate(dataType);
      
      // Create CSV content
      const headers = template.columns.join(',');
      const sampleRow = template.sample.map(row => 
        template.columns.map(col => `"${row[col] || ''}"`).join(',')
      ).join('\n');
      
      const csvContent = `${headers}\n${sampleRow}`;
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataType}_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Template downloaded for ${dataType}`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to download template',
        severity: 'error',
      });
    }
  };

  const handleExecuteImport = async () => {
    if (!uploadedFile) return;

    try {
      setUploading(true);
      const result = await importApi.executeImport(uploadedFile.id, {
        data_type: 'products',
        field_mappings: [],
        skip_header: true,
        date_format: 'DD/MM/YYYY',
        currency: 'GBP',
      });

      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      });

      setPreviewDialog(false);
      setUploadedFile(null);
      setPreviewData(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Import failed',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    const selectedIds = Array.from(selectedIntegrations);
    onNext({
      integrations: selectedIds,
      erpDetails: selectedIntegrations.has('erp') ? erpDetails : null,
      uploadedFile: uploadedFile,
    });
  };

  // Categorize integrations by region
  const ukIntegrations = availableIntegrations.filter(i => i.region === 'UK');
  const usaIntegrations = availableIntegrations.filter(i => i.region === 'USA');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading integrations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
      />

      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mx: 'auto',
            mb: 2,
          }}
        >
          <CloudUpload sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Connect Your Data
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Import your supply chain data or connect to your existing systems
        </Typography>
        <Chip
          label="This step is optional - you can add integrations later"
          color="info"
          size="small"
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Quick Upload Section */}
      <Card sx={{ mb: 4, border: 2, borderColor: 'primary.main', borderStyle: 'dashed' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📁 Quick Start: Upload Your Data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a CSV or Excel file with your products, suppliers, or inventory
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadTemplate('products')}
                size="small"
              >
                Get Template
              </Button>
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </Box>
          </Box>
          
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {uploadedFile && !uploading && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                ✓ File uploaded: <strong>{uploadedFile.filename}</strong>
                {previewData && ` (${previewData.total_rows} rows)`}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* UK Integrations */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        🇬🇧 UK Integrations
        <Chip label={`${ukIntegrations.length} available`} size="small" />
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {ukIntegrations.slice(0, 4).map((integration) => (
          <Grid item xs={12} sm={6} md={3} key={integration.id}>
            <Card
              sx={{
                height: '100%',
                border: 2,
                borderColor: selectedIntegrations.has(integration.id) || integration.status === 'connected'
                  ? theme.palette.primary.main
                  : 'divider',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => handleToggleIntegration(integration.id)}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Storage sx={{ fontSize: 32, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {integration.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {integration.description.substring(0, 50)}...
                </Typography>
                <Chip 
                  label={integration.status === 'connected' ? 'Connected' : 'Available'} 
                  size="small" 
                  color={integration.status === 'connected' ? 'success' : 'default'}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* USA Integrations */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        🇺🇸 USA Integrations
        <Chip label={`${usaIntegrations.length} available`} size="small" />
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {usaIntegrations.slice(0, 4).map((integration) => (
          <Grid item xs={12} sm={6} md={3} key={integration.id}>
            <Card
              sx={{
                height: '100%',
                border: 2,
                borderColor: selectedIntegrations.has(integration.id) || integration.status === 'connected'
                  ? theme.palette.primary.main
                  : 'divider',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => handleToggleIntegration(integration.id)}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CloudQueue sx={{ fontSize: 32, color: theme.palette.secondary.main, mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {integration.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {integration.description.substring(0, 50)}...
                </Typography>
                <Chip 
                  label={integration.status === 'connected' ? 'Connected' : 'Available'} 
                  size="small" 
                  color={integration.status === 'connected' ? 'success' : 'default'}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Selected Integration Configuration */}
      {selectedIntegrations.size > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Configure Selected Integrations
            </Typography>
            
            {Array.from(selectedIntegrations).map(integrationId => {
              const integration = availableIntegrations.find(i => i.id === integrationId);
              if (!integration) return null;
              
              return (
                <Box key={integrationId} sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {integration.name}
                  </Typography>
                  
                  {integration.auth_type === 'api_key' && (
                    <TextField
                      fullWidth
                      size="small"
                      label="API Key"
                      type="password"
                      value={erpDetails.apiKey}
                      onChange={(e) => setErpDetails({ ...erpDetails, apiKey: e.target.value })}
                      placeholder="Enter your API key"
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleConnectIntegration(integration)}
                    disabled={connecting === integration.id || integration.status === 'connected'}
                    startIcon={connecting === integration.id ? <CircularProgress size={16} /> : <CheckCircle />}
                  >
                    {integration.status === 'connected' ? 'Connected' : connecting === integration.id ? 'Connecting...' : 'Connect'}
                  </Button>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Security & Privacy */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Security sx={{ mr: 1, color: theme.palette.success.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Enterprise Security
                </Typography>
              </Box>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary="End-to-end encryption" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary="SOC 2 Type II certified" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary="GDPR compliant" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Speed sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Quick Setup
                </Typography>
              </Box>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.info.main }} />
                  </ListItemIcon>
                  <ListItemText primary="5-minute integration" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.info.main }} />
                  </ListItemIcon>
                  <ListItemText primary="Pre-built connectors" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 16, color: theme.palette.info.main }} />
                  </ListItemIcon>
                  <ListItemText primary="24/7 support available" primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary */}
      {(selectedIntegrations.size > 0 || uploadedFile) && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            ✓ <strong>
              {selectedIntegrations.size} integration(s) selected
              {uploadedFile && `, 1 file uploaded`}
            </strong>
            <br />
            {selectedIntegrations.size > 0 && 'We\'ll sync your data after onboarding.'}
            {uploadedFile && ` ${uploadedFile.filename} ready for import.`}
          </Typography>
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Preview Upload</Typography>
            <IconButton onClick={() => setPreviewDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <>
              <Alert severity={previewData.validation?.valid ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {previewData.validation?.valid 
                  ? `All ${previewData.total_rows} rows are valid and ready to import!`
                  : `${previewData.validation?.error_count || 0} errors, ${previewData.validation?.warning_count || 0} warnings found`
                }
              </Alert>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview (first 10 rows):
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {previewData.columns?.map((col: string) => (
                        <TableCell key={col} sx={{ fontWeight: 600 }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.preview_rows?.map((row: any, idx: number) => (
                      <TableRow key={idx}>
                        {previewData.columns?.map((col: string) => (
                          <TableCell key={col}>{row[col]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleExecuteImport}
            disabled={uploading || !previewData?.validation?.valid}
            startIcon={uploading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {uploading ? 'Importing...' : 'Import Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden submit button for form navigation */}
      <Box sx={{ display: 'none' }}>
        <button onClick={handleNext}>Submit</button>
      </Box>
    </Box>
  );
};

export default DataIntegrationStep;
